import type {ValidationRegistrar} from 'modules/VendorRegister/types';

import {InfoCircleFilled} from '@ant-design/icons';
import {yupResolver} from '@hookform/resolvers/yup';
import {toast} from '@Toast';
import FormInput from 'modules/VendorRegister/components/common/FormInput/FormInput';
import {useCallback, useEffect, useRef} from 'react';
import {useForm} from 'react-hook-form';
import {useAppDispatch} from 'store';
import {sendBankInfo, sendIbanInfo} from 'store/slices/vendor-registration/thunk';
import {p2e} from 'utils/Helper';

import {BANK_INFO_SCHEMA, BANK_NAMES} from './constants';

type BankInfoForm = {
  ibanNumber: string;
  ownerName: string;
  bankName: string;
};

type BankInfoProps = {
  registerValidation: ValidationRegistrar;
};

const BankInfo: React.FC<BankInfoProps> = ({registerValidation}) => {
  const dispatch = useAppDispatch();
  const {control, handleSubmit, watch, setValue} = useForm<BankInfoForm>({
    resolver: yupResolver(BANK_INFO_SCHEMA),
    defaultValues: {
      ibanNumber: '',
      ownerName: '',
      bankName: ''
    }
  });
  const ibanNumber = watch('ibanNumber');
  const ownerName = watch('ownerName');
  const bankName = watch('bankName');
  const ibanRequestRef = useRef(0);

  const submitBankInfo = useCallback(async () => {
    try {
      const response = await dispatch(
        sendBankInfo({
          accountOwner: ownerName,
          ibanNumber: `IR${p2e(ibanNumber)}`
        })
      ).unwrap();

      if (response.isSuccess) {
        return true;
      }

      toast(response.message || 'خطا');
    } catch (error) {
      toast(
        typeof error === 'object' &&
          error !== null &&
          'message' in error &&
          typeof error.message === 'string'
          ? error.message
          : 'خطا'
      );
    }

    return false;
  }, [dispatch, ibanNumber, ownerName]);

  const validateStep = useCallback(() => {
    return new Promise<boolean>((resolve) => {
      void handleSubmit(
        async () => {
          resolve(await submitBankInfo());
        },
        () => resolve(false)
      )();
    });
  }, [handleSubmit, submitBankInfo]);

  useEffect(() => {
    registerValidation(validateStep);
  }, [registerValidation, validateStep]);

  useEffect(() => {
    const requestId = ++ibanRequestRef.current;
    const normalizedIban = p2e(ibanNumber).replace(/^IR/i, '');

    if (normalizedIban.length !== 24) {
      setValue('ownerName', '');
      setValue('bankName', '');
      return;
    }

    let cancelled = false;

    void dispatch(sendIbanInfo({iban: `IR${normalizedIban}`}))
      .unwrap()
      .then((response) => {
        if (cancelled || requestId !== ibanRequestRef.current) {
          return;
        }

        if (!response.isSuccess) {
          setValue('ownerName', '');
          setValue('bankName', '');
          toast(response.message || 'خطا');
          return;
        }

        const firstOwner = response.data?.owners?.[0];
        setValue(
          'ownerName',
          [firstOwner?.firstName, firstOwner?.lastName].filter(Boolean).join(' ')
        );
        setValue('bankName', response.data?.bank ?? '');
      })
      .catch((error) => {
        if (!cancelled && requestId === ibanRequestRef.current) {
          setValue('ownerName', '');
          setValue('bankName', '');
          toast(error.message || 'خطا');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, ibanNumber, setValue]);

  return (
    <div className="flex flex-col gap-2.5 px-4 py-3">
      <FormInput
        name="ibanNumber"
        control={control}
        label="شماره شبا"
        placeholder="شماره شبا خود را وارد کنید"
        type="tel"
        inputMode="numeric"
        maxLength={24}
        suffix="IR"
        required
        inputClassName="text-left"
      />
      {ownerName ? (
        <div className="flex items-center gap-1 text-body-xs text-neutral-fg">
          <InfoCircleFilled aria-hidden />
          <span>
            {ownerName}
            {BANK_NAMES[bankName] ? ` - ${BANK_NAMES[bankName]}` : ''}
          </span>
        </div>
      ) : null}
    </div>
  );
};

export default BankInfo;
